// components/workspace-timeline/WorkspaceTimelineTrackBlock.tsx
import React, { useCallback, useMemo } from 'react';
import {
  areResolvedTimelineTracksEqual,
  areTimelineRangePresentationsEqual,
  areTimelineToolStatesEqual,
  areTimelineTrackBlockStatesEqual,
  buildTrackWaveformBins,
  clamp,
  toAudioGainTrackColor,
} from './presentation';
import type {
  ResolvedWorkspaceTimelineTrack,
  TimelineRangePresentation,
  TimelineToolState,
  TimelineTrackBlockActions,
  TimelineTrackBlockState,
} from './types';

interface WorkspaceTimelineWaveformProps {
  trackId: string;
  bins: number[];
  insetPx: number;
  isActive: boolean;
  isVisible: boolean;
}

const WorkspaceTimelineWaveform = React.memo(({
  trackId,
  bins,
  insetPx,
  isActive,
  isVisible,
}: WorkspaceTimelineWaveformProps) => {
  const waveformStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    left: `${insetPx}px`,
    right: `${insetPx}px`,
    top: '4px',
    bottom: '2px',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1px',
    opacity: isActive ? 0.92 : 0.72,
    overflow: 'hidden',
  }), [insetPx, isActive]);
  const barColor = isVisible ? 'rgba(236,236,236,0.42)' : 'rgba(236,236,236,0.18)';

  return (
    <div className="pointer-events-none" style={waveformStyle}>
      {bins.map((value, index) => (
        <div
          key={`${trackId}-wave-${index}`}
          style={{
            flex: 1,
            minWidth: '1px',
            borderRadius: '0.75px',
            height: `${Math.max(10, Math.round(value * 100))}%`,
            backgroundColor: barColor,
          }}
        />
      ))}
    </div>
  );
});

WorkspaceTimelineWaveform.displayName = 'WorkspaceTimelineWaveform';

const EMPTY_TIMELINE_WAVEFORM_BINS: number[] = [];

interface WorkspaceTimelineTrackPresentation {
  splitPreviewOffsetPercent: number | null;
  trackStartPercent: number;
  trackWidthPercent: number;
  hasHiddenLeftContent: boolean;
  hasHiddenRightContent: boolean;
  hasFadeInEffect: boolean;
  hasFadeOutEffect: boolean;
  fadeInEndPercent: number;
  fadeOutStartPercent: number;
  fadeOutWidthPercent: number;
  labelOpacity: number;
  waveHorizontalInsetPx: number;
  effectiveTrackFillColor: string;
  trackWindowTitle: string;
  activeWindowTitle: string;
  trimStartTitle: string;
  trimEndTitle: string;
}

interface WorkspaceTimelineTrackBlockProps {
  track: ResolvedWorkspaceTimelineTrack;
  range: TimelineRangePresentation;
  state: TimelineTrackBlockState;
  tools: TimelineToolState;
  actions: TimelineTrackBlockActions;
}

const TIMELINE_TRACK_SHELL_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '1px',
  bottom: '1px',
  backgroundColor: '#262626',
  borderRadius: '6px',
};
const TIMELINE_TRACK_ACTIVE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '100%',
  borderRadius: '6px',
  overflow: 'hidden',
};
const TIMELINE_TRACK_LABEL_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '8px',
  top: '50%',
  transform: 'translateY(-50%)',
  maxWidth: 'calc(100% - 16px)',
  fontSize: 12,
  lineHeight: 1.2,
  color: '#ECECEC',
  zIndex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'default',
  pointerEvents: 'auto',
};
const TIMELINE_TRACK_HIDDEN_OVERLAY_STYLE: React.CSSProperties = {
  backgroundImage: 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.35) 0 5px, rgba(255,255,255,0.06) 5px 10px)',
  opacity: 0.72,
};
const TIMELINE_TRACK_LEFT_HIDDEN_CONTENT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: '8px',
  opacity: 0.9,
  backgroundImage: 'repeating-linear-gradient(135deg, rgba(0,0,0,0.4) 0 2px, rgba(255,255,255,0.14) 2px 4px)',
  borderTopLeftRadius: '6px',
  borderBottomLeftRadius: '6px',
  pointerEvents: 'none',
};
const TIMELINE_TRACK_RIGHT_HIDDEN_CONTENT_STYLE: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  width: '8px',
  opacity: 0.9,
  backgroundImage: 'repeating-linear-gradient(-135deg, rgba(0,0,0,0.4) 0 2px, rgba(255,255,255,0.14) 2px 4px)',
  borderTopRightRadius: '6px',
  borderBottomRightRadius: '6px',
  pointerEvents: 'none',
};
const TIMELINE_TRACK_SEPARATING_OVERLAY_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 50%, rgba(255,255,255,0.18) 100%), repeating-linear-gradient(-45deg, rgba(255,255,255,0.14) 0 5px, rgba(255,255,255,0.03) 5px 10px)',
  opacity: 0.85,
};
const TIMELINE_TRACK_MERGING_OVERLAY_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.2) 100%), repeating-linear-gradient(-45deg, rgba(255,255,255,0.16) 0 5px, rgba(255,255,255,0.03) 5px 10px)',
  boxShadow: 'inset 0 0 0 1px rgba(236,236,236,0.6)',
  opacity: 0.92,
};
const TIMELINE_TRACK_FADE_IN_REGION_BASE_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.04))',
};
const TIMELINE_TRACK_FADE_OUT_REGION_BASE_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(to left, rgba(0,0,0,0.55), rgba(0,0,0,0.04))',
};
const TIMELINE_TRACK_SPLIT_SHADE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  right: 0,
  top: 0,
  bottom: 0,
  opacity: 0.9,
  backgroundImage: 'repeating-linear-gradient(-135deg, rgba(0,0,0,0.4) 0 2px, rgba(255,255,255,0.14) 2px 4px)',
  pointerEvents: 'none',
};
const TIMELINE_TRACK_SPLIT_LINE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '2px',
  transform: 'translateX(-1px)',
  backgroundColor: '#ECECEC',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
  pointerEvents: 'none',
};
const TIMELINE_TRACK_TRIM_START_HANDLE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 2,
  left: 0,
  top: 0,
  bottom: 0,
  width: '6px',
  borderTopLeftRadius: '6px',
  borderBottomLeftRadius: '6px',
  backgroundColor: 'rgba(236,236,236,0.55)',
};
const TIMELINE_TRACK_TRIM_END_HANDLE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 2,
  right: 0,
  top: 0,
  bottom: 0,
  width: '6px',
  borderTopRightRadius: '6px',
  borderBottomRightRadius: '6px',
  backgroundColor: 'rgba(236,236,236,0.55)',
};
const TIMELINE_TRACK_FADE_HANDLE_BASE_STYLE: React.CSSProperties = {
  position: 'absolute',
  zIndex: 2,
  top: 0,
  bottom: 0,
  width: '3px',
  transform: 'translateX(-1px)',
  backgroundColor: '#ECECEC',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.5)',
  opacity: 0.95,
};

const areTimelineTrackBlockPropsEqual = (
  previous: WorkspaceTimelineTrackBlockProps,
  next: WorkspaceTimelineTrackBlockProps
): boolean => (
  areResolvedTimelineTracksEqual(previous.track, next.track) &&
  areTimelineRangePresentationsEqual(previous.range, next.range) &&
  areTimelineTrackBlockStatesEqual(previous.state, next.state) &&
  areTimelineToolStatesEqual(previous.tools, next.tools) &&
  previous.actions === next.actions
);

export const WorkspaceTimelineTrackBlock = React.memo(({
  track,
  range,
  state,
  tools,
  actions,
}: WorkspaceTimelineTrackBlockProps) => {
  const { startMs: timelineRenderRangeStartMs, rangeMs: timelineRenderRangeMs } = range;
  const {
    splitPreviewMs,
    isActive,
    isDragging,
    isSelected,
    isSeparatingSoundTrack,
    isMergingTrack,
    splitToolCursor,
  } = state;
  const {
    isSplitToolActive,
    isSoundSeparationToolActive,
    isSoundVolumeToolActive,
    isSoundToolActive,
  } = tools;
  const { onStartTrackDrag, onTrackContextMenu } = actions;
  const presentation = useMemo<WorkspaceTimelineTrackPresentation>(() => {
    const splitPreviewOffsetPercent = splitPreviewMs === null
      ? null
      : clamp(((splitPreviewMs - track.startMs) / Math.max(1, track.activeDurationMs)) * 100, 0, 100);
    const rawTrackStartPercent = timelineRenderRangeMs > 0
      ? ((track.startMs - timelineRenderRangeStartMs) / timelineRenderRangeMs) * 100
      : 0;
    const rawTrackWidthPercent = timelineRenderRangeMs > 0
      ? Math.max((track.activeDurationMs / timelineRenderRangeMs) * 100, 0.8)
      : 0.8;
    const trackWidthPercent = clamp(rawTrackWidthPercent, 0, 100);
    const hasHiddenLeftContent = track.layerType !== 'image' && track.sourceInMs > 1;
    const hasHiddenRightContent =
      track.layerType !== 'image' &&
      track.mediaDurationMs !== null &&
      track.sourceOutMs < (track.mediaDurationMs - 1);
    const hasFadeInEffect = track.layerType !== 'audio' && track.fadeInRatio > 0.0005;
    const hasFadeOutEffect = track.layerType !== 'audio' && track.fadeOutRatio > 0.0005;
    const fadeInEndPercent = hasFadeInEffect
      ? clamp(((track.fadeInEndMs - track.startMs) / Math.max(1, track.activeDurationMs)) * 100, 0, 100)
      : 0;
    const fadeOutStartPercent = hasFadeOutEffect
      ? clamp(((track.fadeOutStartMs - track.startMs) / Math.max(1, track.activeDurationMs)) * 100, 0, 100)
      : 100;
    const labelOpacity = trackWidthPercent < 7 ? 0 : (trackWidthPercent < 12 ? 0.72 : 0.94);
    const waveHorizontalInsetPx = trackWidthPercent < 6 ? 2 : (trackWidthPercent < 12 ? 4 : 7);
    const trackFillColor = track.layerType === 'audio'
      ? toAudioGainTrackColor(track.audioGain, isActive)
      : track.layerType === 'image'
        ? (isActive ? '#666666' : '#5C5C5C')
        : (isActive ? '#444444' : '#3A3A3A');
    const effectiveTrackFillColor = track.isLayerVisible ? trackFillColor : '#2A2A2A';

    return {
      splitPreviewOffsetPercent,
      trackStartPercent: clamp(rawTrackStartPercent, 0, 100),
      trackWidthPercent,
      hasHiddenLeftContent,
      hasHiddenRightContent,
      hasFadeInEffect,
      hasFadeOutEffect,
      fadeInEndPercent,
      fadeOutStartPercent,
      fadeOutWidthPercent: Math.max(0, 100 - fadeOutStartPercent),
      labelOpacity,
      waveHorizontalInsetPx,
      effectiveTrackFillColor,
      trackWindowTitle: `Track window ${Math.round(track.startMs)}-${Math.round(track.endMs)}ms`,
      activeWindowTitle: `Active ${Math.round(track.startMs)}-${Math.round(track.endMs)}ms`,
      trimStartTitle: hasHiddenLeftContent ? 'Set keep start (more source on left)' : 'Set keep start',
      trimEndTitle: hasHiddenRightContent ? 'Set keep end (more source on right)' : 'Set keep end',
    };
  }, [isActive, splitPreviewMs, timelineRenderRangeMs, timelineRenderRangeStartMs, track]);
  const cursor = isSplitToolActive
    ? splitToolCursor
    : ((isSoundToolActive || isSoundSeparationToolActive || isSoundVolumeToolActive) ? 'default' : 'grab');
  const handleCursor = isSplitToolActive ? splitToolCursor : 'ew-resize';
  const pointerEvents = isSplitToolActive ? 'none' : 'auto';
  const audioWaveBins = useMemo(() => {
    if (track.layerType !== 'audio') return EMPTY_TIMELINE_WAVEFORM_BINS;
    return buildTrackWaveformBins(
      track.layerId,
      Math.max(54, Math.min(96, Math.round(presentation.trackWidthPercent * 1.4)))
    );
  }, [presentation.trackWidthPercent, track.layerId, track.layerType]);
  const videoInputRangeOverlays = useMemo(() => {
    if (track.layerType !== 'video' || !track.isLayerVisible) return [];
    return (range.videoInputTimelineRanges ?? [])
      .map((videoInputRange) => {
        const overlapStartMs = Math.max(videoInputRange.startMs, track.startMs);
        const overlapEndMs = Math.min(videoInputRange.endMs, track.endMs);
        if (overlapEndMs <= overlapStartMs) return null;
        const rangeStartPercent = clamp(
          ((overlapStartMs - track.startMs) / Math.max(1, track.activeDurationMs)) * 100,
          0,
          100
        );
        const rangeEndPercent = clamp(
          ((overlapEndMs - track.startMs) / Math.max(1, track.activeDurationMs)) * 100,
          0,
          100
        );
        return {
          ...videoInputRange,
          leftPercent: rangeStartPercent,
          widthPercent: Math.max(0.6, rangeEndPercent - rangeStartPercent),
          showStart: videoInputRange.startMs >= track.startMs && videoInputRange.startMs <= track.endMs,
          showEnd: videoInputRange.endMs >= track.startMs && videoInputRange.endMs <= track.endMs,
        };
      })
      .filter((videoInputRange): videoInputRange is NonNullable<typeof videoInputRange> => Boolean(videoInputRange));
  }, [
    range.videoInputTimelineRangeSignature,
    range.videoInputTimelineRanges,
    track.activeDurationMs,
    track.endMs,
    track.isLayerVisible,
    track.layerType,
    track.startMs,
  ]);
  const trackShellStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_SHELL_BASE_STYLE,
    left: `${presentation.trackStartPercent}%`,
    width: `${presentation.trackWidthPercent}%`,
    opacity: track.isLayerVisible ? (isDragging ? 1 : 0.9) : 0.65,
    boxShadow: isSelected ? '0 0 0 1.5px #ECECEC' : 'none',
    cursor,
    pointerEvents,
  }), [cursor, isDragging, isSelected, pointerEvents, presentation.trackStartPercent, presentation.trackWidthPercent, track.isLayerVisible]);
  const activeRegionStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_ACTIVE_BASE_STYLE,
    backgroundColor: presentation.effectiveTrackFillColor,
    opacity: track.isLayerVisible ? (isActive || isDragging ? 1 : 0.85) : 0.55,
    cursor,
    pointerEvents,
  }), [cursor, isActive, isDragging, pointerEvents, presentation.effectiveTrackFillColor, track.isLayerVisible]);
  const labelStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_LABEL_BASE_STYLE,
    opacity: track.isLayerVisible ? presentation.labelOpacity : Math.min(0.6, presentation.labelOpacity),
  }), [presentation.labelOpacity, track.isLayerVisible]);
  const fadeInRegionStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_FADE_IN_REGION_BASE_STYLE,
    width: `${presentation.fadeInEndPercent}%`,
    opacity: track.isLayerVisible ? 0.65 : 0.4,
  }), [presentation.fadeInEndPercent, track.isLayerVisible]);
  const fadeOutRegionStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_FADE_OUT_REGION_BASE_STYLE,
    width: `${presentation.fadeOutWidthPercent}%`,
    opacity: track.isLayerVisible ? 0.65 : 0.4,
  }), [presentation.fadeOutWidthPercent, track.isLayerVisible]);
  const splitShadeStyle = useMemo<React.CSSProperties | null>(() => {
    if (presentation.splitPreviewOffsetPercent === null) return null;
    return {
      ...TIMELINE_TRACK_SPLIT_SHADE_BASE_STYLE,
      left: `${presentation.splitPreviewOffsetPercent}%`,
    };
  }, [presentation.splitPreviewOffsetPercent]);
  const splitLineStyle = useMemo<React.CSSProperties | null>(() => {
    if (presentation.splitPreviewOffsetPercent === null) return null;
    return {
      ...TIMELINE_TRACK_SPLIT_LINE_BASE_STYLE,
      left: `${presentation.splitPreviewOffsetPercent}%`,
    };
  }, [presentation.splitPreviewOffsetPercent]);
  const trimStartHandleStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_TRIM_START_HANDLE_BASE_STYLE,
    cursor: handleCursor,
    pointerEvents,
  }), [handleCursor, pointerEvents]);
  const trimEndHandleStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_TRIM_END_HANDLE_BASE_STYLE,
    cursor: handleCursor,
    pointerEvents,
  }), [handleCursor, pointerEvents]);
  const fadeInHandleStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_FADE_HANDLE_BASE_STYLE,
    left: `${presentation.fadeInEndPercent}%`,
    cursor: handleCursor,
    pointerEvents,
  }), [handleCursor, pointerEvents, presentation.fadeInEndPercent]);
  const fadeOutHandleStyle = useMemo<React.CSSProperties>(() => ({
    ...TIMELINE_TRACK_FADE_HANDLE_BASE_STYLE,
    left: `${presentation.fadeOutStartPercent}%`,
    cursor: handleCursor,
    pointerEvents,
  }), [handleCursor, pointerEvents, presentation.fadeOutStartPercent]);
  const handleMoveMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onStartTrackDrag(track, 'move-track', event);
  }, [onStartTrackDrag, track]);
  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onTrackContextMenu(event, track);
  }, [onTrackContextMenu, track]);
  const handleTrimStartMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onStartTrackDrag(track, 'trim-blue-start', event);
  }, [onStartTrackDrag, track]);
  const handleTrimEndMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onStartTrackDrag(track, 'trim-blue-end', event);
  }, [onStartTrackDrag, track]);
  const handleFadeInMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onStartTrackDrag(track, 'fade-in-end', event);
  }, [onStartTrackDrag, track]);
  const handleFadeOutMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onStartTrackDrag(track, 'fade-out-start', event);
  }, [onStartTrackDrag, track]);
  const dragMouseDown = isSplitToolActive || isSoundToolActive ? undefined : handleMoveMouseDown;
  const trimStartMouseDown = isSplitToolActive || isSoundToolActive ? undefined : handleTrimStartMouseDown;
  const trimEndMouseDown = isSplitToolActive || isSoundToolActive ? undefined : handleTrimEndMouseDown;
  const fadeInMouseDown = isSplitToolActive || isSoundToolActive ? undefined : handleFadeInMouseDown;
  const fadeOutMouseDown = isSplitToolActive || isSoundToolActive ? undefined : handleFadeOutMouseDown;

  return (
    <div
      role="button"
      tabIndex={-1}
      onMouseDown={dragMouseDown}
      onContextMenu={handleContextMenu}
      style={trackShellStyle}
      title={presentation.trackWindowTitle}
    >
      <div
        role="button"
        tabIndex={-1}
        onMouseDown={dragMouseDown}
        style={activeRegionStyle}
        title={presentation.activeWindowTitle}
      >
        {videoInputRangeOverlays.map((videoInputRange) => (
          <div
            key={`${track.layerId}-${videoInputRange.id}`}
            className="pointer-events-none"
            style={{
              position: 'absolute',
              left: `${videoInputRange.leftPercent}%`,
              width: `${videoInputRange.widthPercent}%`,
              top: '0px',
              bottom: '0px',
              borderLeft: videoInputRange.showStart ? '2px dashed #ECECEC' : '1px dashed rgba(236,236,236,0.35)',
              borderRight: videoInputRange.showEnd ? '2px dashed #ECECEC' : '1px dashed rgba(236,236,236,0.35)',
              backgroundColor: 'rgba(255,255,255,0.08)',
            }}
            title={`${videoInputRange.label}: ${Math.round(videoInputRange.startMs)}-${Math.round(videoInputRange.endMs)}ms`}
          />
        ))}
        <div className="select-none" style={labelStyle} title={track.name}>
          {track.name}
          {isSeparatingSoundTrack ? ' (splitting...)' : ''}
        </div>
        {audioWaveBins.length > 0 && (
          <WorkspaceTimelineWaveform
            trackId={track.layerId}
            bins={audioWaveBins}
            insetPx={presentation.waveHorizontalInsetPx}
            isActive={isActive}
            isVisible={track.isLayerVisible}
          />
        )}
        {!track.isLayerVisible && (
          <div
            className="pointer-events-none absolute inset-0"
            style={TIMELINE_TRACK_HIDDEN_OVERLAY_STYLE}
            title="Layer hidden"
          />
        )}
        {presentation.hasHiddenLeftContent && (
          <div
            style={TIMELINE_TRACK_LEFT_HIDDEN_CONTENT_STYLE}
            title="Trimmed from start (more source available)"
          />
        )}
        {presentation.hasHiddenRightContent && (
          <div
            style={TIMELINE_TRACK_RIGHT_HIDDEN_CONTENT_STYLE}
            title="Trimmed from end (more source available)"
          />
        )}
        {isSeparatingSoundTrack && (
          <div
            className="pointer-events-none absolute inset-0 animate-pulse"
            style={TIMELINE_TRACK_SEPARATING_OVERLAY_STYLE}
            title="Splitting sound..."
          />
        )}
        {isMergingTrack && (
          <div
            className="pointer-events-none absolute inset-0 animate-pulse"
            style={TIMELINE_TRACK_MERGING_OVERLAY_STYLE}
            title="Merging tracks..."
          />
        )}
        {presentation.hasFadeInEffect && (
          <div
            className="pointer-events-none absolute top-0 bottom-0 left-0"
            style={fadeInRegionStyle}
            title="Fade-in region"
          />
        )}
        {presentation.hasFadeOutEffect && (
          <div
            className="pointer-events-none absolute top-0 bottom-0 right-0"
            style={fadeOutRegionStyle}
            title="Fade-out region"
          />
        )}
        {isSplitToolActive && splitShadeStyle !== null && (
          <div
            style={splitShadeStyle}
            title="This section will be in the right segment after split"
          />
        )}
        {isSplitToolActive && splitLineStyle !== null && <div style={splitLineStyle} />}
        <div
          role="button"
          tabIndex={-1}
          onMouseDown={trimStartMouseDown}
          style={trimStartHandleStyle}
          title={presentation.trimStartTitle}
        />
        <div
          role="button"
          tabIndex={-1}
          onMouseDown={trimEndMouseDown}
          style={trimEndHandleStyle}
          title={presentation.trimEndTitle}
        />
        {presentation.hasFadeInEffect && (
          <div
            role="button"
            tabIndex={-1}
            onMouseDown={fadeInMouseDown}
            style={fadeInHandleStyle}
            title="Adjust fade-in length"
          />
        )}
        {presentation.hasFadeOutEffect && (
          <div
            role="button"
            tabIndex={-1}
            onMouseDown={fadeOutMouseDown}
            style={fadeOutHandleStyle}
            title="Adjust fade-out length"
          />
        )}
      </div>
    </div>
  );
}, areTimelineTrackBlockPropsEqual);

WorkspaceTimelineTrackBlock.displayName = 'WorkspaceTimelineTrackBlock';
